/**
 * Section d'une page : un titre (niveau calculé), une phrase d'usage, un
 * compteur, une petite action, puis son contenu dans une carte. Repliable
 * pour le contenu secondaire ; son état ouvert suit l'ancre de l'adresse.
 * Remplace les cartes titrées et les `<details>` écrits à la main.
 */
import { useId, useState, type ReactNode } from 'react';
import { HeadingLevel, useHeadingLevel } from './PageHeader.js';
import { ActionButton, isLinkAction, type Action } from './action.js';

type Heading = 'h2' | 'h3' | 'h4';

export function Section(props: {
  title?: string;
  /** Une ligne qui dit à quoi sert la section. */
  hint?: ReactNode;
  count?: number;
  badge?: ReactNode;
  /** Une petite action propre à la section (« Ajouter »). */
  action?: Action;
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Ancre stable : `#id` ouvre la section repliée. */
  id?: string;
  tone?: 'default' | 'attention';
  /** Sans surface : le contenu (une liste groupée) porte déjà la sienne. */
  flush?: boolean;
  children: ReactNode;
}): ReactNode {
  const level = useHeadingLevel();
  const Heading = (`h${Math.min(level, 4)}` as Heading);
  const generatedId = useId();
  const titleId = `${props.id ?? generatedId}-titre`;
  const anchored = typeof window !== 'undefined' && props.id !== undefined && window.location.hash === `#${props.id}`;
  const [open, setOpen] = useState(props.defaultOpen ?? anchored);
  const classes = [
    'ui-section',
    props.flush ? 'ui-section--flush' : 'ui-section--card',
    props.tone === 'attention' && 'ui-section--attention',
    props.collapsible && 'ui-section--collapsible',
  ]
    .filter(Boolean)
    .join(' ');
  const heading = props.title ? (
    <span className="ui-section__heading">
      <Heading id={titleId} className="ui-section__title">
        {props.title}
        {props.count !== undefined ? <span className="ui-section__count">{props.count}</span> : null}
      </Heading>
      {props.badge}
    </span>
  ) : null;
  const action = props.action ? (
    <ActionButton action={props.action} variant={isLinkAction(props.action) ? 'ghost' : 'secondary'} />
  ) : null;
  const body = <HeadingLevel.Provider value={level + 1}>{props.children}</HeadingLevel.Provider>;

  if (props.collapsible) {
    return (
      <section className={classes} id={props.id} aria-labelledby={props.title ? titleId : undefined}>
        <button
          type="button"
          className="ui-section__toggle"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {heading}
          <span className="ui-section__chevron" aria-hidden="true" />
        </button>
        {props.hint ? <p className="ui-section__hint">{props.hint}</p> : null}
        {open ? (
          <div className="ui-section__body">
            {action ? <div className="ui-section__action">{action}</div> : null}
            {body}
          </div>
        ) : null}
      </section>
    );
  }
  return (
    <section className={classes} id={props.id} aria-labelledby={props.title ? titleId : undefined}>
      {heading || action ? (
        <div className="ui-section__header">
          {heading}
          {action}
        </div>
      ) : null}
      {props.hint ? <p className="ui-section__hint">{props.hint}</p> : null}
      <div className="ui-section__body">{body}</div>
    </section>
  );
}

/** Surface simple, sans titre (un titre appelle une Section). */
export function Card(props: {
  children: ReactNode;
  as?: 'div' | 'article' | 'li';
  className?: string;
  tone?: 'default' | 'attention' | 'muted';
}): ReactNode {
  const Tag = props.as ?? 'div';
  const classes = ['card', props.tone && props.tone !== 'default' && `card--${props.tone}`, props.className]
    .filter(Boolean)
    .join(' ');
  return <Tag className={classes}>{props.children}</Tag>;
}
